import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Key, X, Check } from "@phosphor-icons/react";

const ROLES = [
  { value: "inventory_user", label: "Inventory User" },
  { value: "supervisor", label: "Supervisor" },
  { value: "admin", label: "Administrator" },
];

// iter21: password strength mirror — must match backend `_validate_new_password`.
const RULES = [
  { label: "At least 12 characters", test: (p) => p.length >= 12 },
  { label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "Digit", test: (p) => /[0-9]/.test(p) },
  { label: "Symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function ResetPasswordDialog({ target, onClose, onSuccess }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const ruleState = RULES.map((r) => ({ ...r, ok: r.test(pw) }));
  const allOk = ruleState.every((r) => r.ok);
  const match = pw.length > 0 && pw === pw2;
  const ready = allOk && match && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      await api.post(`/users/${target.id}/reset-password`, {
        new_password: pw, confirm_password: pw2,
      });
      toast.success(`Temporary password set for ${target.email}. They will be forced to change it on next login.`);
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50" onClick={onClose}>
      <form
        data-testid="reset-pw-dialog"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card-flat border-2 border-primary p-5 w-full max-w-md m-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-tech">Reset Password</div>
            <div className="font-display font-black text-lg mt-0.5">{target.name}</div>
            <div className="font-mono text-xs text-muted-foreground">{target.email}</div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-muted"><X size={14} /></button>
        </div>

        <label className="block mb-3">
          <div className="label-tech mb-1">New temporary password</div>
          <input
            data-testid="reset-pw-new" type="password" autoComplete="new-password"
            value={pw} onChange={(e) => setPw(e.target.value)} required
            className="w-full h-11 px-3 border border-border bg-background rounded-sm font-mono text-sm"
          />
        </label>
        <label className="block mb-3">
          <div className="label-tech mb-1">Confirm temporary password</div>
          <input
            data-testid="reset-pw-confirm" type="password" autoComplete="new-password"
            value={pw2} onChange={(e) => setPw2(e.target.value)} required
            className="w-full h-11 px-3 border border-border bg-background rounded-sm font-mono text-sm"
          />
          {pw2.length > 0 && !match && (
            <div className="text-xs text-destructive mt-1 font-mono">Passwords do not match</div>
          )}
        </label>

        <div className="border border-border rounded-sm p-3 bg-muted/40 mb-3">
          <div className="label-tech mb-2">Password requirements</div>
          <ul className="space-y-1 text-xs font-mono">
            {ruleState.map((r) => (
              <li key={r.label} className="flex items-center gap-2">
                {r.ok ? <Check size={12} weight="bold" className="text-success" /> : <X size={12} weight="bold" className="text-muted-foreground" />}
                <span className={r.ok ? "text-success" : "text-muted-foreground"}>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-[10px] font-mono text-muted-foreground border border-yellow-500/40 bg-yellow-500/5 rounded-sm p-2 mb-3">
          The user will be forced to change this to their own private password on next login. All their existing sessions will be invalidated.
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-hard h-11 px-4 border border-border font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-muted">Cancel</button>
          <button
            type="submit" disabled={!ready}
            data-testid="reset-pw-submit"
            className="btn-hard flex-1 h-11 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-50"
          >
            {busy ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "inventory_user" });
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", password: "", role: "inventory_user" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary">← Admin</Link>
      <div className="my-3">
        <div className="label-tech mb-1">Access Control</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Users & Roles</h1>
      </div>

      <div className="card-flat border-border p-4 mb-4">
        <div className="label-tech mb-2">Create user</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="h-11 px-3 border border-border bg-background rounded-sm text-sm" />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="h-11 px-3 border border-border bg-background rounded-sm text-sm" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="h-11 px-3 border border-border bg-background rounded-sm text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 px-2 border border-border bg-background rounded-sm text-sm">
            {ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
          <button onClick={create} className="btn-hard h-11 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white flex items-center gap-1">
            <Plus size={14} weight="bold" /> Create
          </button>
        </div>
      </div>

      <div className="card-flat border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 border-b border-border">
            <tr className="text-left label-tech">
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2">
                  <select
                    value={u.role}
                    onChange={(e) => updateRole(u.id, e.target.value)}
                    className="h-8 px-2 border border-border bg-background rounded-sm text-xs"
                  >
                    {ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                  </select>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {/* iter21: hide reset for demo pseudo-role. The current admin uses the
                      personal Change Password page (available from the header). */}
                  {u.role !== "demo" && (
                    <button
                      data-testid={`reset-pw-btn-${u.id}`}
                      onClick={() => setResetTarget(u)}
                      className="text-primary hover:underline text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1"
                    >
                      <Key size={12} weight="bold" /> Reset Password
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetTarget && (
        <ResetPasswordDialog
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onSuccess={() => { setResetTarget(null); load(); }}
        />
      )}
    </div>
  );
}
